import type { KindReason, MailMessageView, MailThreadView, MessageKind } from "@kwp/contracts";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { domainError, errorMessage, useTRPC } from "../../lib/api";
import { Button, cx, ErrorText } from "../../ui";
import { Icon, IconButton, type IconName } from "../../ui/icons";
import { draftFromMessage, type RuleSourceMessage } from "../settings/rules/model";
import { RuleDialog } from "../settings/rules/RuleDialog";
import type { ActTarget } from "./use-mail-actions";
import { kindReasonText } from "./kinds";
import { RawDialog } from "./RawDialog";
import { SenderKindDialog, SenderStrip } from "./SenderKind";

/**
 * One conversation, bodies fetched live (`mail.getThread`, architecture §6.4) and kept in memory
 * for the session by the query cache. The newest and any unread message start expanded.
 */
/** `return`: back to the inbox from Archived, Spam or Trash (Move to inbox, Not spam, Restore). */
export type ThreadCommand = "archive" | "trash" | "spam" | "star" | "unread" | "move" | "tag" | "reply" | "forward" | "task" | "share" | "snooze" | "unsnooze" | "return";

const VIEW_CACHE = { staleTime: Infinity, gcTime: 30 * 60_000 } as const;

export function useThreadView(threadId: string | undefined) {
  const trpc = useTRPC();
  return useQuery(trpc.mail.getThread.queryOptions(threadId === undefined ? skipToken : { threadId }, VIEW_CACHE));
}

/**
 * Starts reading a conversation before it is opened (a pointer resting on its row, or the
 * keyboard on it), into the same cache entry `useThreadView` reads: opening it then shows at once.
 * One that is cached or already loading is not asked for again.
 */
export function usePrefetchThread(): (threadId: string) => void {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useCallback(
    (threadId: string) => {
      void queryClient.prefetchQuery(trpc.mail.getThread.queryOptions({ threadId }, VIEW_CACHE));
    },
    [queryClient, trpc],
  );
}

/** The action target for a loaded thread, when it is not (or no longer) in the list. */
export function targetOf(view: MailThreadView, tags: readonly string[]): ActTarget {
  const prefix = `${view.accountId}:`;
  return {
    id: view.id,
    accountId: view.accountId,
    messageIds: view.messages.map((m) => m.id.slice(prefix.length)),
    tags: [...tags],
  };
}

export function ThreadView({
  thread,
  subject,
  starred,
  snoozed,
  returnLabel = null,
  kindReason = null,
  sender = null,
  kind = null,
  newSender = false,
  onBack,
  onCommand,
  children,
}: {
  thread: ReturnType<typeof useThreadView>;
  /** From the list row while the bodies load. */
  subject: string | null;
  starred: boolean;
  /** The conversation is snoozed (its list row says so): the toolbar offers "Unsnooze now". */
  snoozed: boolean;
  /** In Archived, Spam or Trash: the button that takes the conversation back to the inbox. */
  returnLabel?: string | null;
  /** Why the conversation is where it is (People, Notifications…), from its list row (WP-061). */
  kindReason?: KindReason | null;
  /** Whose mail this is, for "This sender is…" (WP-063); null when it is only the mailbox's own. */
  sender?: string | null;
  /** The conversation's kind, from its list row (Keep keeps Claude's move there). */
  kind?: MessageKind | null;
  /** The first mail from its sender (WP-063): the strip asks what the sender is (WP-065). */
  newSender?: boolean;
  onBack: () => void;
  onCommand: (command: ThreadCommand) => void;
  /** The message cards, rendered by the caller from the loaded view. */
  children: (view: MailThreadView) => ReactNode;
}) {
  const view = thread.data === undefined ? null : thread.data;
  const title = view === null ? subject : view.subject;
  // "This sender is…": open, with a kind preselected (the strip's buttons) or none (`null`).
  const [choosing, setChoosing] = useState<{ kind: MessageKind | null } | null>(null);

  return (
    <div className="flex flex-col pb-20 md:pb-0">
      <div className="sticky top-0 z-10 flex h-11 items-center gap-1 border-b border-border bg-bg">
        <IconButton icon="back" label="Back" title="Back (Esc)" className="size-8" onClick={onBack} aria-keyshortcuts="Escape" />
        <h1 className="min-w-0 flex-1 truncate px-1 text-base font-semibold text-text" title={title ?? undefined}>
          {title === null || title === "" ? "(no subject)" : title}
        </h1>
        <ThreadToolbar
          starred={starred}
          snoozed={snoozed}
          returnLabel={returnLabel}
          onCommand={onCommand}
          ruleSource={view === null ? null : ruleSourceOf(view)}
          originalId={view === null ? null : newestId(view)}
          onSenderKind={sender === null ? null : () => setChoosing({ kind: null })}
        />
      </div>
      {sender === null || kind === null ? null : (
        <SenderStrip address={sender} newSender={newSender} kind={kind} suggested={kindReason === "suggested"} onChoose={(k) => setChoosing({ kind: k })} />
      )}
      {kindReason === null ? null : (
        <details className="pt-2 text-xs text-muted">
          <summary className="cursor-pointer">Why here?</summary>
          <p className="pt-1">
            {kindReasonText(kindReason)}{" "}
            {sender === null ? null : (
              <button type="button" className="text-accent underline" onClick={() => setChoosing({ kind: null })}>
                This sender is…
              </button>
            )}
          </p>
        </details>
      )}
      {choosing !== null && sender !== null ? (
        <SenderKindDialog
          address={sender}
          {...(choosing.kind === null ? {} : { initialKind: choosing.kind })}
          reason={kindReason}
          onClose={() => setChoosing(null)}
        />
      ) : null}
      {thread.isPending ? <p className="text-sm text-muted">Loading the conversation…</p> : null}
      {thread.isError ? <ThreadError error={thread.error} /> : null}
      {view === null ? null : children(view)}
    </div>
  );
}

function ThreadError({ error }: { error: unknown }) {
  const reason = domainError(error)?.reason;
  const text =
    reason === "thread_not_cached"
      ? "This conversation has not synced yet. Try again in a moment."
      : reason === "needs_reconnect"
        ? "This account needs to be reconnected in Settings → Accounts."
        : `The conversation could not be loaded: ${errorMessage(error)}`;
  return <ErrorText>{text}</ErrorText>;
}

const TOOLS: ReadonlyArray<{ command: ThreadCommand; label: string; key: string; icon: IconName; gap?: boolean }> = [
  { command: "reply", label: "Reply", key: "r", icon: "reply" },
  { command: "forward", label: "Forward", key: "f", icon: "forward" },
  { command: "archive", label: "Archive", key: "e", icon: "archive", gap: true },
  { command: "trash", label: "Trash", key: "#", icon: "trash" },
  { command: "snooze", label: "Snooze", key: "z", icon: "snooze" },
  { command: "unsnooze", label: "Unsnooze now", key: "", icon: "unsnooze" },
  { command: "star", label: "Star", key: "s", icon: "star" },
  { command: "unread", label: "Mark unread", key: "u", icon: "unread" },
];

/** The rarer actions, behind "More". */
const MORE: ReadonlyArray<{ command: ThreadCommand; label: string; key: string; icon: IconName }> = [
  { command: "move", label: "Move", key: "v", icon: "move" },
  { command: "tag", label: "Tag", key: "l", icon: "tag" },
  { command: "task", label: "Create task", key: "T", icon: "task" },
  { command: "share", label: "Share to chat", key: "", icon: "share" },
  { command: "spam", label: "Spam", key: "!", icon: "spam" },
];

const withKey = (label: string, key: string) => (key === "" ? label : `${label} (${key})`);

/** Actions for the open conversation as icons (name and key on hover): a toolbar on wide screens, a bottom bar on a phone. */
/** "Make a rule from this" uses the newest message: mail from its sender, on the account it came to. */
function ruleSourceOf(view: MailThreadView): RuleSourceMessage | null {
  const newest = view.messages[view.messages.length - 1];
  return newest === undefined ? null : { from: newest.from, accountId: newest.accountId };
}

/** "Show original" shows the newest message's headers. */
function newestId(view: MailThreadView): string | null {
  const newest = view.messages[view.messages.length - 1];
  return newest === undefined ? null : newest.id;
}

function ThreadToolbar({
  starred,
  snoozed,
  returnLabel,
  onCommand,
  ruleSource,
  originalId,
  onSenderKind,
}: {
  starred: boolean;
  snoozed: boolean;
  returnLabel: string | null;
  onCommand: (command: ThreadCommand) => void;
  ruleSource: RuleSourceMessage | null;
  originalId: string | null;
  onSenderKind: (() => void) | null;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Conversation actions"
      className={cx(
        "fixed inset-x-0 bottom-0 z-20 flex items-center gap-0.5 overflow-x-auto border-t border-border bg-surface px-2 py-1.5",
        "md:static md:overflow-visible md:border-0 md:bg-transparent md:px-0 md:py-0",
      )}
    >
      {returnLabel === null ? null : (
        <Button variant="ghost" className="shrink-0" onClick={() => onCommand("return")}>
          {returnLabel}
        </Button>
      )}
      {TOOLS.filter((t) => t.command !== "unsnooze" || snoozed).map((t) => {
        const label = t.command === "star" && starred ? "Starred" : t.label;
        return (
          <span key={t.command} className="flex items-center">
            {t.gap === true ? <span aria-hidden="true" className="mx-1 h-4 w-px bg-border" /> : null}
            <IconButton
              icon={t.icon}
              label={label}
              tone={t.command === "star" && starred ? "active" : "plain"}
              className="size-8"
              title={withKey(label, t.key)}
              filled={t.command === "star" && starred}
              aria-keyshortcuts={t.key === "" ? undefined : t.key}
              aria-pressed={t.command === "star" ? starred : undefined}
              onClick={() => onCommand(t.command)}
            />
          </span>
        );
      })}
      <span aria-hidden="true" className="mx-1 h-4 w-px bg-border" />
      <MoreMenu onCommand={onCommand} ruleSource={ruleSource} originalId={originalId} onSenderKind={onSenderKind} />
    </div>
  );
}

function MoreMenu({
  onCommand,
  ruleSource,
  originalId,
  onSenderKind,
}: {
  onCommand: (command: ThreadCommand) => void;
  ruleSource: RuleSourceMessage | null;
  originalId: string | null;
  /** "This sender is…" (WP-063); null when the conversation has no sender but the mailbox. */
  onSenderKind: (() => void) | null;
}) {
  const [open, setOpen] = useState(false);
  const [rule, setRule] = useState(false);
  const [original, setOriginal] = useState(false);
  // Fixed to the window, next to the button: the conversation pane scrolls and would clip it.
  const [place, setPlace] = useState<CSSProperties>({});
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: PointerEvent) => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(e) => {
        // Escape closes the menu, not the conversation; keys typed in a dialog stay there.
        if (rule || original) {
          e.stopPropagation();
          return;
        }
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <IconButton
        icon="more"
        label="More actions"
        className="size-8"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const right = Math.max(8, window.innerWidth - r.right);
          // Open downward, or upward when the button is low on the screen (the phone's bottom bar).
          setPlace(r.top > window.innerHeight / 2 ? { right, bottom: window.innerHeight - r.top + 4 } : { right, top: r.bottom + 4 });
          setOpen((o) => !o);
        }}
      />
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="More actions"
          style={place}
          className="fixed z-30 w-56 rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {MORE.map((m) => (
            <button
              key={m.command}
              type="button"
              role="menuitem"
              aria-keyshortcuts={m.key === "" ? undefined : m.key}
              onClick={() => {
                setOpen(false);
                onCommand(m.command);
              }}
              className="flex min-h-9 w-full items-center gap-3 px-3 text-left text-sm text-text hover:bg-surface-2"
            >
              <Icon name={m.icon} className="text-muted" />
              <span className="flex-1">{m.label}</span>
              {m.key === "" ? null : <kbd className="text-xs text-muted">{m.key}</kbd>}
            </button>
          ))}
          {ruleSource === null ? null : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setRule(true);
              }}
              className="flex min-h-9 w-full items-center gap-3 border-t border-border px-3 text-left text-sm text-text hover:bg-surface-2"
            >
              <Icon name="rule" className="text-muted" />
              <span className="flex-1">Make a rule from this</span>
            </button>
          )}
          {onSenderKind === null ? null : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSenderKind();
              }}
              className="flex min-h-9 w-full items-center gap-3 px-3 text-left text-sm text-text hover:bg-surface-2"
            >
              <Icon name="person" className="text-muted" />
              <span className="flex-1">This sender is…</span>
            </button>
          )}
          {originalId === null ? null : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setOriginal(true);
              }}
              className="flex min-h-9 w-full items-center gap-3 px-3 text-left text-sm text-text hover:bg-surface-2"
            >
              <Icon name="more" className="text-muted" />
              <span className="flex-1">Show original</span>
            </button>
          )}
        </div>
      ) : null}
      {original && originalId !== null ? <RawDialog messageId={originalId} onClose={() => setOriginal(false)} /> : null}
      {rule && ruleSource !== null ? <RuleDialog draft={draftFromMessage(ruleSource)} onClose={() => setRule(false)} /> : null}
    </div>
  );
}

/** Which messages start expanded: the newest, and every unread one. */
export function initiallyExpanded(messages: readonly MailMessageView[]): Set<string> {
  const ids = new Set(messages.filter((m) => m.unread).map((m) => m.id));
  const last = messages[messages.length - 1];
  if (last !== undefined) ids.add(last.id);
  return ids;
}

/**
 * The thread's messages as cards, newest on top (Clayton, 2026-10-15); older messages start
 * folded to one line. Reply is the toolbar's (and `r`).
 */
export function MessageStack({
  view,
  render,
}: {
  view: MailThreadView;
  render: (m: MailMessageView, expanded: boolean, toggle: () => void) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(() => initiallyExpanded(view.messages));
  const newestFirst = [...view.messages].reverse();
  return (
    <div className="flex flex-col gap-2 pt-3">
      {newestFirst.map((m) =>
        render(m, expanded.has(m.id), () =>
          setExpanded((all) => {
            const next = new Set(all);
            if (next.has(m.id)) next.delete(m.id);
            else next.add(m.id);
            return next;
          }),
        ),
      )}
    </div>
  );
}
